import type { Attribute } from '../../../shared/types';

export type MappingTableDensity = 'narrow' | 'compact' | 'regular';

export interface MappingTableColumnWidths {
  checkbox: number;
  order: number;
  pcfAttribute: string;
  revitParam: string;
  type: number;
  fx: number;
  actions: number;
}

export interface MappingTableFilters {
  pcfAttributeName: string;
  revitParam: string;
  attributeType: string;
  fx: string;
}

export interface MappingSortConfig {
  key: keyof Attribute;
  direction: 'asc' | 'desc';
}

export function normalizeAttributeToken(value: string): string {
  return value.trim().toUpperCase().replace(/[\s_]+/g, '-');
}

/**
 * Returns column widths for the mapping table based on the current density.
 *
 * Numeric widths are derived from theme componentSizes when possible;
 * percentage widths stay flexible so the table reflows across viewports.
 */
export function getColumnWidths(density: MappingTableDensity): MappingTableColumnWidths {
  if (density === 'narrow') {
    return {
      checkbox: 24,
      order: 0,
      pcfAttribute: '23%',
      revitParam: '31%',
      type: 76,
      fx: 36,
      actions: 28,
    };
  }

  if (density === 'compact') {
    return {
      checkbox: 28,
      order: 30,
      pcfAttribute: '25%',
      revitParam: '33%',
      type: 84,
      fx: 52,
      actions: 30,
    };
  }

  return {
    checkbox: 32,
    order: 36,
    pcfAttribute: '32%',
    revitParam: '40%',
    type: 96,
    fx: 92,
    actions: 36,
  };
}

interface FilterAttributesOptions {
  attributes: Attribute[];
  filters: MappingTableFilters;
  workspaceLens: 'all' | 'focused';
  normalizedHighlightedAttributes: string[];
}

/**
 * Filters the attribute list by the active text filters and workspace lens.
 */
export function filterAttributes(options: FilterAttributesOptions): Attribute[] {
  const { attributes, filters, workspaceLens, normalizedHighlightedAttributes } = options;

  return attributes.filter((attribute) => {
    const normalizedAttributeName = normalizeAttributeToken(attribute.pcfAttributeName);

    if (workspaceLens === 'focused' && normalizedHighlightedAttributes.length > 0) {
      if (!normalizedHighlightedAttributes.includes(normalizedAttributeName)) {
        return false;
      }
    }

    return (
      attribute.pcfAttributeName.toLowerCase().includes(filters.pcfAttributeName.toLowerCase()) &&
      attribute.revitParam.toLowerCase().includes(filters.revitParam.toLowerCase()) &&
      (filters.attributeType === '' || attribute.attributeType === filters.attributeType) &&
      attribute.fx.toLowerCase().includes(filters.fx.toLowerCase())
    );
  });
}

interface SortAttributesOptions {
  attributes: Attribute[];
  sortConfig: MappingSortConfig | null;
}

/**
 * Sorts the attribute list by the active sort configuration.
 */
export function sortAttributes(options: SortAttributesOptions): Attribute[] {
  const { attributes, sortConfig } = options;

  if (sortConfig === null) {
    return attributes;
  }

  const toComparable = (value: Attribute[keyof Attribute]): string | number =>
    typeof value === 'number' ? value : String(value ?? '');

  const sorted = [...attributes];
  sorted.sort((a, b) => {
    const aValue = toComparable(a[sortConfig.key]);
    const bValue = toComparable(b[sortConfig.key]);
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return sorted;
}

/**
 * Filters and sorts attributes in one pass for the mapping table view.
 */
export function getVisibleAttributes(
  attributes: Attribute[],
  filters: MappingTableFilters,
  sortConfig: MappingSortConfig | null,
  workspaceLens: 'all' | 'focused',
  highlightedAttributeNames: string[]
): Attribute[] {
  const normalizedHighlightedAttributes = highlightedAttributeNames
    .map(normalizeAttributeToken)
    .filter(Boolean);

  const filtered = filterAttributes({
    attributes,
    filters,
    workspaceLens,
    normalizedHighlightedAttributes,
  });

  return sortAttributes({ attributes: filtered, sortConfig });
}
